import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["provider-directory-v2"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      providerDirectoryV2DirectoryCreateService: build.mutation<
        ProviderDirectoryV2DirectoryCreateServiceApiResponse,
        ProviderDirectoryV2DirectoryCreateServiceApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-directory/v2/directory-services/create/`,
          method: "POST",
          body: queryArg.providerDirectoryV2DirectoryCreateRequest,
        }),
        invalidatesTags: ["provider-directory-v2"],
      }),
      providerDirectoryV2CreateService: build.mutation<
        ProviderDirectoryV2CreateServiceApiResponse,
        ProviderDirectoryV2CreateServiceApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-directory/v2/provider-services/create/`,
          method: "POST",
          body: queryArg.providerDirectoryCreateServiceRequestV2,
        }),
        invalidatesTags: ["provider-directory-v2"],
      }),
      providerDirectoryV2Resolve: build.query<
        ProviderDirectoryV2ResolveApiResponse,
        ProviderDirectoryV2ResolveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/provider-directory/v2/search/resolve/`,
          params: {
            address: queryArg.address,
            intent: queryArg.intent,
            limit: queryArg.limit,
            matter_id: queryArg.matterId,
            q: queryArg.q,
          },
        }),
        providesTags: ["provider-directory-v2"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type ProviderDirectoryV2DirectoryCreateServiceApiResponse =
  /** status 200  */ ProviderDirectoryV2DirectoryCreateResponse;
export type ProviderDirectoryV2DirectoryCreateServiceApiArg = {
  providerDirectoryV2DirectoryCreateRequest: ProviderDirectoryV2DirectoryCreateRequest;
};
export type ProviderDirectoryV2CreateServiceApiResponse = /** status 200  */
  | ProviderDirectoryCreateServiceResponseV2
  | /** status 201  */ ProviderDirectoryCreateServiceResponseV2;
export type ProviderDirectoryV2CreateServiceApiArg = {
  providerDirectoryCreateServiceRequestV2: ProviderDirectoryCreateServiceRequestV2;
};
export type ProviderDirectoryV2ResolveApiResponse =
  /** status 200  */ ProviderServiceResolveResponseV2;
export type ProviderDirectoryV2ResolveApiArg = {
  /** Optional address hint used to pick a recommended match. */
  address?: string;
  /** Routing intent for best-contact resolution. */
  intent?: "BILLING" | "CLINICAL" | "GENERAL" | "RECORDS";
  /** Max candidate pool size (default 25, max 100). */
  limit?: number;
  /** Matter UUID for already_on_matter flag. */
  matterId?: string;
  /** Primary provider name query. */
  q: string;
};
export type ProviderDirectoryV2DirectoryCreateResponse = {
  provider_location_id: string;
  provider_service_id: string;
  reused_existing: boolean;
  matched_provider_service_id?: string | null;
};
export type AddressDraft = {
  formatted_address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  google_place_id?: string | null;
};
export type ProviderCreateResolutionMode = "USE_EXISTING" | "CREATE_ANYWAY";
export type DedupMatchSource =
  | "place_id"
  | "exact_topology"
  | "strong_name_address"
  | "strong_name_phone"
  | "fuzzy_name_address"
  | "fuzzy_phone";
export type ProviderDedupCandidate = {
  provider_service_id: string;
  name: string;
  address_summary?: string | null;
  organization_name?: string | null;
  match_source: DedupMatchSource;
  similarity_score?: number | null;
};
export type ProviderDirectoryCreateConflictResponseV2 = {
  error_code: "DUPLICATE_CANDIDATES";
  detail: string;
  candidates: ProviderDedupCandidate[];
};
export type ProviderDirectoryV2DirectoryCreateRequest = {
  /** * `internal_existing` - internal_existing
   * `google_place` - google_place
   * `manual` - manual */
  source: "internal_existing" | "google_place" | "manual";
  base_provider_service_id?: string | null;
  service_name: string;
  /** * `ER` - ER
   * `RADIOLOGY` - RADIOLOGY
   * `CHIROPRACTIC` - CHIROPRACTIC
   * `ORTHOPEDIC` - ORTHOPEDIC
   * `PHYSICAL_THERAPY` - PHYSICAL_THERAPY
   * `PRIMARY_CARE` - PRIMARY_CARE
   * `PHARMACY` - PHARMACY
   * `IMAGING` - IMAGING
   * `SURGERY` - SURGERY
   * `SPECIALIST` - SPECIALIST
   * `GENERAL` - GENERAL */
  service_type?:
    | (
        | "ER"
        | "RADIOLOGY"
        | "CHIROPRACTIC"
        | "ORTHOPEDIC"
        | "PHYSICAL_THERAPY"
        | "PRIMARY_CARE"
        | "PHARMACY"
        | "IMAGING"
        | "SURGERY"
        | "SPECIALIST"
        | "GENERAL"
        | null
      )
    | null;
  /** Explicit resolution after a 409 Conflict */
  resolution_mode?: ProviderCreateResolutionMode | null;
  /** Required when resolution_mode=USE_EXISTING */
  selected_candidate_provider_service_id?: string | null;
  /** Required when resolution_mode=CREATE_ANYWAY */
  create_anyway_reason?: string | null;
  address: AddressDraft;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
};
export type ProviderDirectoryCreateServiceResponseV2 = {
  provider_location_id: string;
  provider_service_id: string;
  medical_provider_id: string;
  reused_existing?: boolean;
  matched_provider_service_id?: string | null;
};
export type ProviderDirectoryAddressDraftV2 = {
  formatted_address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  google_place_id?: string | null;
};
export type ProviderDirectoryCreateServiceRequestV2 = {
  /** Matter UUID */
  matter_id: string;
  /** internal_existing|google_place|manual */
  source: string;
  /** Existing ProviderService id when initialized from internal search */
  base_provider_service_id?: string | null;
  service_name: string;
  /** ServiceType enum value (default GENERAL) */
  service_type?: string | null;
  /** Optional provider type hint: HOSPITAL_SYSTEM|CLINIC_INDEPENDENT */
  provider_type_hint?: string | null;
  /** @deprecated Use resolution_mode=CREATE_ANYWAY instead */
  allow_create_anyway?: boolean;
  /** Explicit resolution after a 409 Conflict */
  resolution_mode?: ProviderCreateResolutionMode | null;
  /** Required when resolution_mode=USE_EXISTING */
  selected_candidate_provider_service_id?: string | null;
  /** Required when resolution_mode=CREATE_ANYWAY */
  create_anyway_reason?: string | null;
  address: ProviderDirectoryAddressDraftV2;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
};
export type ProviderServiceSummaryV2 = {
  id: string | string;
  name: string;
  service_type?: string | null;
  provider_type_hint?: string | null;
  portal_eligible?: boolean;
  portal_type?: string[] | null;
};
export type ProviderOrganizationSummaryV2 = {
  id: string | string;
  name: string;
};
export type ProviderLocationAddressSnippetV2 = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  formatted_address?: string | null;
};
export type ProviderLocationSummaryV2 = {
  id: string | string;
  name: string;
  location_type?: string | null;
  status?: string | null;
  address?: ProviderLocationAddressSnippetV2;
  has_active_request_plan?: boolean | null;
};
export type ContactPointSummaryV2 = {
  id: string | string;
  point_type: string;
  purpose: string;
  value?: string | null;
  normalized_value?: string | null;
  name?: string | null;
  is_preferred?: boolean;
  voice_ai_cadence_days?: number | null;
  verified_at?: string | null;
  verification_source?: string | null;
  sources?:
    | {
        [key: string]: any;
      }[]
    | null;
};
export type ProviderServiceBestContactsV2 = {
  fax?: ContactPointSummaryV2;
  email?: ContactPointSummaryV2;
  phone?: ContactPointSummaryV2;
  mailing_address?: ProviderLocationAddressSnippetV2;
};
export type DestinationStatusEnum = "VERIFIED" | "UNVERIFIED" | "MISSING";
export type ProviderServiceSearchResultV2 = {
  provider_service: ProviderServiceSummaryV2;
  organization?: ProviderOrganizationSummaryV2;
  clinical_location?: ProviderLocationSummaryV2;
  billing_location?: ProviderLocationSummaryV2;
  records_location?: ProviderLocationSummaryV2;
  best_contacts?: ProviderServiceBestContactsV2;
  verified_contact_count?: number;
  matter_link_count?: number;
  has_preferred_records?: boolean;
  has_preferred_bills?: boolean;
  records_destination_status?: DestinationStatusEnum | null;
  bills_destination_status?: DestinationStatusEnum | null;
  subtitle?: string | null;
  already_on_matter?: boolean;
};
export type ProviderServiceResolveResponseV2 = {
  query: string;
  address?: string | null;
  matter_id?: string | null;
  recommended?: ProviderServiceSearchResultV2;
  alternates?: ProviderServiceSearchResultV2[];
};
export const {
  useProviderDirectoryV2DirectoryCreateServiceMutation,
  useProviderDirectoryV2CreateServiceMutation,
  useProviderDirectoryV2ResolveQuery,
} = injectedRtkApi;
